import React, { useEffect, useState } from "react";

import { Gauge } from "lucide-react";

import type {
  QueueLane,
  QueueSlots,
} from "../../Store/Reducers/AccountPlanAccessSlice";

interface Props {
  queue: QueueSlots;
  saving: boolean;
  onSave: (read: number | null, brief: number | null, note: string) => void;
}

const secs = (s: number) =>
  s < 60
    ? `${s} s`
    : s < 3600
      ? `${Math.round(s / 60)} min`
      : `${Math.round(s / 3600)} h`;

const LANES: { key: "read" | "brief"; label: string; hint: string }[] = [
  {
    key: "read",
    label: "Reading plans",
    hint: "Each one runs LibreOffice and makes slide pictures: memory-heavy.",
  },
  {
    key: "brief",
    label: "Writing briefings",
    hint: "AI only, light on memory; limited by the AI provider's quota.",
  },
];

/**
 * Processing slots (spec QUE-08..10): how many plans are read and briefed at once across
 * every organization. Set here, capped by the server, live on every server within the
 * cache; no deploy. Live numbers beside each lane so the setting is tuned with evidence.
 */
const QueueSlotsPanel: React.FC<Props> = ({ queue, saving, onSave }) => {
  const [read, setRead] = useState<string>(
    queue.read.set ? String(queue.read.set) : ""
  );
  const [brief, setBrief] = useState<string>(
    queue.brief.set ? String(queue.brief.set) : ""
  );
  const [note, setNote] = useState("");
  useEffect(() => {
    setRead(queue.read.set ? String(queue.read.set) : "");
    setBrief(queue.brief.set ? String(queue.brief.set) : "");
  }, [queue.read.set, queue.brief.set]);

  const values = { read, brief };
  const setters = { read: setRead, brief: setBrief };
  const parse = (v: string) => (v.trim() ? Number(v) : null);
  const invalid = (lane: QueueLane, v: string) => {
    const n = parse(v);
    return n !== null && (!Number.isInteger(n) || n < 1 || n > lane.ceiling);
  };
  const bad = invalid(queue.read, read) || invalid(queue.brief, brief);
  const changed =
    parse(read) !== queue.read.set || parse(brief) !== queue.brief.set;

  return (
    <section
      aria-labelledby="ap-slots-title"
      className="mb-4 rounded-lg border border-gray-200 bg-white p-4"
      data-testid="queue-slots"
    >
      <div className="mb-3 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-[#8538d4]">
          <Gauge size={18} />
        </div>
        <div className="min-w-0">
          <h3
            id="ap-slots-title"
            className="text-base font-semibold text-gray-900"
          >
            Processing slots
          </h3>
          <p className="max-w-3xl text-sm text-gray-600">
            How many plans are read and briefed at once, for every organization
            together. Every server applies a change within about{" "}
            {queue.cache_seconds} seconds; no deploy. The server&apos;s own
            setting is the ceiling, so this can&apos;t overload it. Leave a box
            empty to use the server&apos;s setting.
          </p>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {LANES.map(({ key, label, hint }) => {
          const lane = queue[key];
          const err = invalid(lane, values[key]);
          return (
            <div key={key} className="rounded-md border border-gray-100 p-3">
              <label
                htmlFor={`slots-${key}`}
                className="text-sm font-medium text-gray-900"
              >
                {label}
              </label>
              <p className="text-xs text-gray-500">{hint}</p>
              <div className="mt-2 flex items-center gap-2">
                <input
                  id={`slots-${key}`}
                  inputMode="numeric"
                  value={values[key]}
                  onChange={e => setters[key](e.target.value)}
                  placeholder={String(lane.ceiling)}
                  aria-invalid={err}
                  aria-describedby={`slots-${key}-help`}
                  className={`w-20 rounded-md border px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 ${err ? "border-rose-400" : "border-gray-300"}`}
                />
                <span
                  id={`slots-${key}-help`}
                  className="text-xs text-gray-500"
                >
                  at once · now {lane.effective} · server allows up to{" "}
                  {lane.ceiling}
                </span>
              </div>
              {err && (
                <p role="alert" className="mt-1 text-xs text-rose-700">
                  Between 1 and {lane.ceiling} on this server.
                </p>
              )}
              <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-gray-600">
                <dt>Running now</dt>
                <dd className="font-mono">{lane.running}</dd>
                <dt>Waiting</dt>
                <dd className="font-mono">{lane.queued}</dd>
                <dt>Oldest wait now</dt>
                <dd className="font-mono">{secs(lane.oldest_wait_s)}</dd>
                <dt>Longest wait, 24 h</dt>
                <dd className="font-mono">{secs(lane.longest_wait_24h_s)}</dd>
              </dl>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-[240px] flex-1">
          <label
            htmlFor="slots-note"
            className="text-xs font-medium text-gray-700"
          >
            Note (optional, kept in the log)
          </label>
          <input
            id="slots-note"
            value={note}
            onChange={e => setNote(e.target.value)}
            maxLength={300}
            placeholder="Why this change"
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
          />
        </div>
        <button
          type="button"
          disabled={saving || bad || !changed}
          onClick={() => onSave(parse(read), parse(brief), note)}
          className="rounded-md bg-[#974ae4] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50"
        >
          Save slots
        </button>
      </div>
      {queue.updated_by && (
        <p className="mt-2 text-xs text-gray-500">
          Last changed by {queue.updated_by}
          {queue.updated_at
            ? `, ${new Date(queue.updated_at).toLocaleString()}`
            : ""}
          {queue.note ? ` — “${queue.note}”` : ""}
        </p>
      )}
    </section>
  );
};

export default QueueSlotsPanel;
